import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { axiosInstance } from '../../common/axiosInstans.ts';
import {
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    IconButton,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    FormGroup,
    FormControlLabel,
    Checkbox,
    Switch,
    Typography,
} from '@mui/material';
import UserServices from './services/userService.ts';
import { useGlobalStore } from '../../application/store/store.tsx';
import { useTranslation } from 'react-i18next';
import OrganizationService from '../superAdmin/TenantOrganizations/OrganizationService.ts';

interface Organization {
    id: string;
    name: string;
}

interface User {
    firstName: string;
    lastName: string;
    userName: string;
    email: string;
    barcode?: string;
    userRoles: Array<{ roleId: string }>;
    isActive: boolean;
    organizationIds: string[];
    receiveInventoryReminderEmail?: boolean;
}

interface MergedRole {
    roleId: string;
    roleName: string;
    description: string;
    enabled: boolean;
}

const UserView = () => {
    const { id } = useParams<{ id: string }>();
    const [user, setUser] = useState<User | null>(null);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [mergedRoles, setMergedRoles] = useState<MergedRole[]>([]);
    const [isActive, setIsActive] = useState(false);
    const [organizationList, setOrganizationList] = useState<Organization[]>(
        []
    );
    const [currentUserOrganizations, setCurrentUserOrganizations] = useState<
        Organization[]
    >([]);
    const [formData, setFormData] = useState({
        organizationIds: [] as string[],
    });
    const [originalOrganizationIds, setOriginalOrganizationIds] = useState<
        string[]
    >([]);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editFormData, setEditFormData] = useState({
        firstName: '',
        lastName: '',
        barcode: '',
    });
    const [isSavingUser, setIsSavingUser] = useState(false);
    const [isSavingOrgs, setIsSavingOrgs] = useState(false);
    const [isSavingRoles, setIsSavingRoles] = useState(false);
    const [isTogglingStatus, setIsTogglingStatus] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isDeviationRecipient, setIsDeviationRecipient] = useState(false);
    const [recipientId, setRecipientId] = useState<string | null>(null);
    const [isTogglingDeviationRecipient, setIsTogglingDeviationRecipient] =
        useState(false);
    const [receiveInventoryReminderEmail, setReceiveInventoryReminderEmail] =
        useState(false);
    const [isTogglingInventoryReminder, setIsTogglingInventoryReminder] =
        useState(false);
    const [isLowSaldoRecipient, setIsLowSaldoRecipient] = useState(false);
    const [lowSaldoRecipientId, setLowSaldoRecipientId] = useState<
        string | null
    >(null);
    const [isTogglingLowSaldoRecipient, setIsTogglingLowSaldoRecipient] =
        useState(false);

    const {
        setMessage,
        startLoading,
        stopLoading,
        cachedIndividualUsers,
        setCachedIndividualUser,
        clearUserCache,
        clearInactiveUserCache,
    } = useGlobalStore();

    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

    const getCachedUser = (userId: string) => {
        const cached = cachedIndividualUsers[userId];
        if (!cached) return null;

        const isValid = Date.now() - cached.cacheTime < CACHE_DURATION;
        return isValid ? cached.user : null;
    };
    const hasUpdated = useRef(false);
    const userService = new UserServices();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const organizationService = new OrganizationService();

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const data = await organizationService.getOrganizations();

                // Handle both array and object responses
                const dataArray = Array.isArray(data) ? data : [];

                const simplifiedOrgs: Organization[] = dataArray.map(
                    (org: any) => ({
                        id: org.id,
                        name: org.name,
                    })
                );
                setOrganizationList(simplifiedOrgs);
            } catch (error) {
                console.error('Failed to fetch organizations:', error);
                setOrganizationList([]);
            }
        };

        const fetchUserOrganizations = async (userData?: User) => {
            try {
                let userOrgs = await userService.getUserOrganizations(id!);

                // Handle both array and object responses
                let orgsArray = Array.isArray(userOrgs) ? userOrgs : [];

                // WORKAROUND: If API returns empty array but user has organizationIds,
                // map them to organization objects from the organizationList
                if (
                    orgsArray.length === 0 &&
                    userData?.organizationIds &&
                    userData.organizationIds.length > 0
                ) {
                    // We'll need to wait for organizationList to be populated
                    // This will be handled in a separate useEffect
                    setFormData((prev) => ({
                        ...prev,
                        organizationIds: userData.organizationIds,
                    }));
                    setOriginalOrganizationIds(userData.organizationIds);
                    return;
                }

                // Transform new API format to old format
                // New format: { organizationId, organizationName, isCurrentOrganization }
                // Old format: { id, name }
                const transformedOrgs = orgsArray.map((org: any) => ({
                    id: org.organizationId || org.id,
                    name: org.organizationName || org.name,
                }));

                setCurrentUserOrganizations(transformedOrgs);

                // Set initial organization IDs for the form
                const userOrgIds = transformedOrgs.map((org: any) => org.id);
                setFormData((prev) => ({
                    ...prev,
                    organizationIds: userOrgIds,
                }));
                setOriginalOrganizationIds(userOrgIds);
            } catch (error) {
                console.error('Failed to fetch user organizations:', error);
                // Set empty arrays on error
                setCurrentUserOrganizations([]);
                setFormData((prev) => ({
                    ...prev,
                    organizationIds: [],
                }));
                setOriginalOrganizationIds([]);
            }
        };

        fetchOrganizations();
        fetchUser().then((resp) => {
            if (resp) {
                // Check if user came from cache
                const cachedUser = getCachedUser(id!);
                if (cachedUser) {
                    // User came from cache, set data immediately without loading
                    setUser(resp);
                    setIsActive(resp.isActive);
                    setReceiveInventoryReminderEmail(
                        resp.receiveInventoryReminderEmail ?? false
                    );
                    setIsLoadingData(false);

                    // Still fetch roles and organizations in background but don't show loading
                    fetchAllRoles().then((allRoles) => {
                        setMergedRoles(mergeRoles(resp.userRoles, allRoles));
                    });
                    fetchUserOrganizations(resp);
                    fetchDeviationRecipientStatus(resp);
                    fetchLowSaldoRecipientStatus(resp);
                } else {
                    // User came from API, show loading until all data is fetched
                    fetchAllRoles().then((allRoles) => {
                        setUser(resp);
                        setMergedRoles(mergeRoles(resp.userRoles, allRoles));
                        setIsActive(resp.isActive);
                        setReceiveInventoryReminderEmail(
                            resp.receiveInventoryReminderEmail ?? false
                        );
                        setIsLoadingData(false);
                    });
                    // Pass user data to fetchUserOrganizations for fallback
                    fetchUserOrganizations(resp);
                    fetchDeviationRecipientStatus(resp);
                    fetchLowSaldoRecipientStatus(resp);
                }
            } else {
                setIsLoadingData(false);
            }
        });
    }, [id]);

    const fetchUser = async (): Promise<User | null> => {
        // Check cache first
        const cachedUser = getCachedUser(id!);
        if (cachedUser) {
            return cachedUser;
        }

        // If no valid cache, fetch from API
        try {
            startLoading();
            const userData = await userService.getUser(id);
            if (userData) {
                setCachedIndividualUser(id!, userData);
            }
            return userData;
        } catch (error) {
            setMessage('Kunde inte hämta användare!', 'error', 5000);
            return null;
        } finally {
            stopLoading();
        }
    };

    const fetchDeviationRecipientStatus = async (userData?: User) => {
        try {
            const recipients = await userService.getDeviationEmailRecipients();
            const email = (userData?.email ?? user?.email)
                ?.toLowerCase()
                .trim();
            const match = Array.isArray(recipients)
                ? recipients.find(
                      (r: any) =>
                          r?.email?.toLowerCase().trim() === email &&
                          !!r?.isActive
                  )
                : undefined;
            if (match) {
                const rid =
                    (match as any)?.id ??
                    (match as any)?.recipientId ??
                    (match as any)?.guid ??
                    null;
                setRecipientId(rid);
                setIsDeviationRecipient(true);
            } else {
                setRecipientId(null);
                setIsDeviationRecipient(false);
            }
        } catch (error) {
            setIsDeviationRecipient(false);
        }
    };

    const fetchLowSaldoRecipientStatus = async (userData?: User) => {
        try {
            const recipients = await userService.getLowSaldoEmailRecipients();
            const email = (userData?.email ?? user?.email)
                ?.toLowerCase()
                .trim();
            const match = Array.isArray(recipients)
                ? recipients.find(
                      (r: any) =>
                          r?.email?.toLowerCase().trim() === email &&
                          !!r?.isActive
                  )
                : undefined;
            if (match) {
                const rid =
                    (match as any)?.id ??
                    (match as any)?.recipientId ??
                    (match as any)?.guid ??
                    null;
                setLowSaldoRecipientId(rid);
                setIsLowSaldoRecipient(true);
            } else {
                setLowSaldoRecipientId(null);
                setIsLowSaldoRecipient(false);
            }
        } catch (error) {
            setIsLowSaldoRecipient(false);
        }
    };

    const fetchAllRoles = async () => {
        try {
            startLoading();

            return await userService.getAllRoles();
        } catch (error) {
            setMessage('Kunde inte hämta roller!:', 'error', 5000);
            return [];
        } finally {
            stopLoading();
        }
    };

    function mergeRoles(
        userRoles: Array<{ roleId: string }>,
        availableRoles: any[]
    ): MergedRole[] {
        const userRoleIds = new Set(userRoles.map((role) => role.roleId));

        return availableRoles.map((role) => ({
            roleId: role.id,
            roleName: role.name,
            description: role.description,
            enabled: userRoleIds.has(role.id),
        }));
    }

    const toggleUserStatus = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setIsTogglingStatus(true);
            startLoading();
            await axiosInstance.post(`/users/${id}/toggle-status`, {
                activateUser: e.target.checked,
                userId: id,
            });

            setIsActive(e.target.checked);

            // Update user state with new status
            if (user) {
                const updatedUser = {
                    ...user,
                    isActive: e.target.checked,
                };
                setUser(updatedUser);

                // Update cache with new user data
                setCachedIndividualUser(id!, updatedUser);

                // Clear both user lists since the user moved between active/inactive
                clearUserCache();
                clearInactiveUserCache();
            }
        } catch (error: any) {
            if (error.status === 409) {
                setMessage(
                    'Det går inte inaktivera en super admin. Ta bort användaren som super admin innan du försöker igen',
                    'error',
                    10000
                );
            } else {
                setMessage('Fel vid växling av användarstatus', 'error', 10000);
            }
        } finally {
            setIsTogglingStatus(false);
            stopLoading();
        }
    };

    const getOrgNameById = (id: string): string => {
        const org = organizationList.find((org) => org.id === id);
        return org ? org.name : '';
    };

    const handleRoleChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const previousRoles = [...mergedRoles];
        try {
            const roleName = event.target.name;

            setIsSavingRoles(true);
            startLoading();
            const updatedMergedRoles = mergedRoles.map((role) =>
                role.roleName === roleName
                    ? { ...role, enabled: !role.enabled }
                    : role
            );
            setMergedRoles(updatedMergedRoles);

            hasUpdated.current = true;
            await userService.updateUserRoles(updatedMergedRoles, id);

            // Update user state with new roles
            if (user) {
                const updatedUserRoles = updatedMergedRoles
                    .filter((role) => role.enabled)
                    .map((role) => ({ roleId: role.roleId }));

                const updatedUser = {
                    ...user,
                    userRoles: updatedUserRoles,
                };
                setUser(updatedUser);

                // Update cache with new user data
                setCachedIndividualUser(id!, updatedUser);
            }

            setMessage('Roll Uppdaterad!', 'success', 5000);
        } catch {
            setMessage('Roll updatering misslyckades!', 'error', 5000);
            setMergedRoles(previousRoles);
        } finally {
            setIsSavingRoles(false);
            stopLoading();
        }
    };

    const handleOrgChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;

        const selectedIds =
            typeof value === 'string' ? value.split(',') : value;

        setFormData((prev) => ({
            ...prev,
            organizationIds: selectedIds,
        }));
    };

    const handleSaveOrganizations = async () => {
        try {
            setIsSavingOrgs(true);
            startLoading();
            const result = await userService.updateUserOrganizations(
                id!,
                formData.organizationIds
            );

            // Update the current organizations display
            setCurrentUserOrganizations(result.organizations || []);

            // Update the original state to reflect saved changes
            setOriginalOrganizationIds([...formData.organizationIds]);

            // Update user state if needed
            if (user) {
                const updatedUser = {
                    ...user,
                    organizationIds: formData.organizationIds,
                };
                setUser(updatedUser);

                // Update cache with new user data
                setCachedIndividualUser(id!, updatedUser);
            }

            setMessage('Organisationer uppdaterade!', 'success', 5000);
        } catch (error) {
            setMessage('Kunde inte uppdatera organisationer!', 'error', 5000);
        } finally {
            setIsSavingOrgs(false);
            stopLoading();
        }
    };

    const hasOrganizationChanges = (): boolean => {
        return (
            JSON.stringify(formData.organizationIds.sort()) !==
            JSON.stringify(originalOrganizationIds.sort())
        );
    };

    const handleResendWelcomeEmail = async () => {
        setOpenConfirmDialog(false);
        try {
            setIsSendingEmail(true);
            startLoading();
            await userService.resendWelcomeEmail(id!);
            setMessage('Välkomstmail skickat!', 'success', 5000);
        } catch (error) {
            setMessage('Kunde inte skicka välkomstmail!', 'error', 5000);
        } finally {
            setIsSendingEmail(false);
            stopLoading();
        }
    };

    const handleOpenConfirmDialog = () => {
        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    const handleEditClick = () => {
        if (user) {
            setEditFormData({
                firstName: user.firstName,
                lastName: user.lastName,
                barcode: user.barcode || '',
            });
            setIsEditMode(true);
        }
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditFormData({
            firstName: '',
            lastName: '',
            barcode: '',
        });
    };

    const handleSaveEdit = async () => {
        try {
            setIsSavingUser(true);
            startLoading();
            await userService.updateUser(id!, editFormData);

            // Update local user state
            if (user) {
                const updatedUser = {
                    ...user,
                    firstName: editFormData.firstName,
                    lastName: editFormData.lastName,
                    barcode: editFormData.barcode,
                };
                setUser(updatedUser);

                // Update cache with new user data
                setCachedIndividualUser(id!, updatedUser);

                // Clear user list caches so changes appear in the list
                clearUserCache();
                clearInactiveUserCache();
            }

            setIsEditMode(false);
            setMessage('Användarinformation uppdaterad!', 'success', 5000);
        } catch (error) {
            setMessage(
                'Kunde inte uppdatera användarinformation!',
                'error',
                5000
            );
        } finally {
            setIsSavingUser(false);
            stopLoading();
        }
    };

    const handleEditFieldChange = (field: string, value: string) => {
        setEditFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleDeviationRecipientToggle = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const checked = e.target.checked;
        try {
            setIsTogglingDeviationRecipient(true);
            startLoading();
            if (checked) {
                if (!user) throw new Error('No user');
                const created = await userService.createDeviationEmailRecipient(
                    {
                        email: user.email,
                        name: `${user.firstName} ${user.lastName}`,
                        isActive: true,
                        description: 'Avvikelsemail mottagare',
                    }
                );
                const rid =
                    (created as any)?.id ??
                    (created as any)?.recipientId ??
                    (created as any)?.guid ??
                    null;
                setRecipientId(rid);
                setIsDeviationRecipient(true);
                setMessage('Inställning uppdaterad', 'success', 5000);
            } else {
                if (recipientId) {
                    await userService.deleteDeviationEmailRecipient(
                        recipientId
                    );
                } else {
                    const recipients =
                        await userService.getDeviationEmailRecipients();
                    const toDelete = Array.isArray(recipients)
                        ? recipients.filter(
                              (r: any) =>
                                  r?.email?.toLowerCase().trim() ===
                                  user?.email?.toLowerCase().trim()
                          )
                        : [];
                    for (const m of toDelete) {
                        const idToDelete =
                            (m as any)?.id ??
                            (m as any)?.recipientId ??
                            (m as any)?.guid ??
                            null;
                        if (idToDelete) {
                            await userService.deleteDeviationEmailRecipient(
                                idToDelete
                            );
                        }
                    }
                }
                setIsDeviationRecipient(false);
                setRecipientId(null);
                setMessage('Inställning uppdaterad', 'success', 5000);
            }
            // Skip immediate re-check to avoid stale GET toggling the checkbox back on
        } catch (error) {
            setMessage(
                'Kunde inte uppdatera mottagare för avvikelser',
                'error',
                5000
            );
            setIsDeviationRecipient(!checked);
        } finally {
            setIsTogglingDeviationRecipient(false);
            stopLoading();
        }
    };

    const handleInventoryReminderToggle = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const checked = e.target.checked;
        try {
            setIsTogglingInventoryReminder(true);
            startLoading();
            await userService.updateUser(id!, {
                receiveInventoryReminderEmail: checked,
            });
            setReceiveInventoryReminderEmail(checked);

            // Update user state and cache
            if (user) {
                const updatedUser = {
                    ...user,
                    receiveInventoryReminderEmail: checked,
                };
                setUser(updatedUser);
                setCachedIndividualUser(id!, updatedUser);
            }

            setMessage(t('users.edit.notifications.updated'), 'success', 5000);
        } catch (error) {
            setMessage(
                t('users.edit.notifications.updateFailed'),
                'error',
                5000
            );
            setReceiveInventoryReminderEmail(!checked);
        } finally {
            setIsTogglingInventoryReminder(false);
            stopLoading();
        }
    };

    const handleLowSaldoRecipientToggle = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const checked = e.target.checked;
        try {
            setIsTogglingLowSaldoRecipient(true);
            startLoading();
            if (checked) {
                if (!user) throw new Error('No user');
                const created = await userService.createLowSaldoEmailRecipient({
                    email: user.email,
                    name: `${user.firstName} ${user.lastName}`,
                    isActive: true,
                    description: 'Mottagare av lågt saldo-notiser',
                });
                const rid =
                    (created as any)?.id ??
                    (created as any)?.recipientId ??
                    (created as any)?.guid ??
                    null;
                setLowSaldoRecipientId(rid);
                setIsLowSaldoRecipient(true);
                setMessage('Inställning uppdaterad', 'success', 5000);
            } else {
                if (lowSaldoRecipientId) {
                    await userService.deleteLowSaldoEmailRecipient(
                        lowSaldoRecipientId
                    );
                } else {
                    const recipients =
                        await userService.getLowSaldoEmailRecipients();
                    const toDelete = Array.isArray(recipients)
                        ? recipients.filter(
                              (r: any) =>
                                  r?.email?.toLowerCase().trim() ===
                                  user?.email?.toLowerCase().trim()
                          )
                        : [];
                    for (const m of toDelete) {
                        const idToDelete =
                            (m as any)?.id ??
                            (m as any)?.recipientId ??
                            (m as any)?.guid ??
                            null;
                        if (idToDelete) {
                            await userService.deleteLowSaldoEmailRecipient(
                                idToDelete
                            );
                        }
                    }
                }
                setIsLowSaldoRecipient(false);
                setLowSaldoRecipientId(null);
                setMessage('Inställning uppdaterad', 'success', 5000);
            }
        } catch (error) {
            setMessage(
                'Kunde inte uppdatera mottagare för lågt saldo',
                'error',
                5000
            );
            setIsLowSaldoRecipient(!checked);
        } finally {
            setIsTogglingLowSaldoRecipient(false);
            stopLoading();
        }
    };

    // Removed redundant useEffect that was causing duplicate API calls

    return (
        <div className="px-4 py-2">
            <div className="max-w-4xl mx-auto">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('..')}
                    color="primary"
                    className="mb-4"
                >
                    Tillbaka
                </Button>

                {/* User Information Card */}
                <Card className="mb-4">
                    <CardContent>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                        >
                            <Typography variant="h5" component="h1">
                                Användarinformation
                            </Typography>
                            {!isEditMode && (
                                <IconButton
                                    color="primary"
                                    onClick={handleEditClick}
                                    size="small"
                                >
                                    <EditIcon />
                                </IconButton>
                            )}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {isEditMode ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    label="Förnamn"
                                    value={editFormData.firstName}
                                    onChange={(e) =>
                                        handleEditFieldChange(
                                            'firstName',
                                            e.target.value
                                        )
                                    }
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Efternamn"
                                    value={editFormData.lastName}
                                    onChange={(e) =>
                                        handleEditFieldChange(
                                            'lastName',
                                            e.target.value
                                        )
                                    }
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Streckkod"
                                    value={editFormData.barcode}
                                    onChange={(e) =>
                                        handleEditFieldChange(
                                            'barcode',
                                            e.target.value
                                        )
                                    }
                                    fullWidth
                                    helperText="Valfritt - används för att identifiera användaren med streckkodsläsare"
                                />
                                <Box display="flex" gap={1} mt={1}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={
                                            isSavingUser ? (
                                                <CircularProgress
                                                    size={16}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <SaveIcon />
                                            )
                                        }
                                        onClick={handleSaveEdit}
                                        disabled={isSavingUser}
                                    >
                                        {isSavingUser ? 'Sparar...' : 'Spara'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={handleCancelEdit}
                                        disabled={isSavingUser}
                                    >
                                        Avbryt
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <Box mb={2}>
                                    {isLoadingData ? (
                                        <Skeleton
                                            variant="text"
                                            width="60%"
                                            height={40}
                                        />
                                    ) : (
                                        <Typography variant="h6" gutterBottom>
                                            {user?.firstName} {user?.lastName}
                                        </Typography>
                                    )}
                                </Box>

                                <Box mb={2}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Användarnamn
                                    </Typography>
                                    {isLoadingData ? (
                                        <Skeleton variant="text" width="40%" />
                                    ) : (
                                        <Typography variant="body1">
                                            {user?.userName}
                                        </Typography>
                                    )}
                                </Box>

                                <Box mb={2}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        E-post
                                    </Typography>
                                    {isLoadingData ? (
                                        <Skeleton variant="text" width="50%" />
                                    ) : (
                                        <Typography variant="body1">
                                            {user?.email}
                                        </Typography>
                                    )}
                                </Box>

                                <Box mb={2}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Streckkod
                                    </Typography>
                                    {isLoadingData ? (
                                        <Skeleton variant="text" width="30%" />
                                    ) : (
                                        <Typography variant="body1">
                                            {user?.barcode || (
                                                <span className="text-gray-400 italic">
                                                    Ingen streckkod angiven
                                                </span>
                                            )}
                                        </Typography>
                                    )}
                                </Box>

                                <Box
                                    mb={2}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isDeviationRecipient}
                                                onChange={
                                                    handleDeviationRecipientToggle
                                                }
                                                disabled={
                                                    isTogglingDeviationRecipient ||
                                                    isLoadingData
                                                }
                                            />
                                        }
                                        label={t(
                                            'users.edit.notifications.deviationEmail.label'
                                        )}
                                    />
                                    {isTogglingDeviationRecipient && (
                                        <CircularProgress size={20} />
                                    )}
                                </Box>

                                <Box
                                    mb={2}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    receiveInventoryReminderEmail
                                                }
                                                onChange={
                                                    handleInventoryReminderToggle
                                                }
                                                disabled={
                                                    isTogglingInventoryReminder ||
                                                    isLoadingData
                                                }
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography>
                                                    {t(
                                                        'users.edit.notifications.inventoryReminder.label'
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {t(
                                                        'users.edit.notifications.inventoryReminder.description'
                                                    )}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    {isTogglingInventoryReminder && (
                                        <CircularProgress size={20} />
                                    )}
                                </Box>

                                <Box
                                    mb={2}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isLowSaldoRecipient}
                                                onChange={
                                                    handleLowSaldoRecipientToggle
                                                }
                                                disabled={
                                                    isTogglingLowSaldoRecipient ||
                                                    isLoadingData
                                                }
                                            />
                                        }
                                        label="Motta email vid lågt saldo"
                                    />
                                    {isTogglingLowSaldoRecipient && (
                                        <CircularProgress size={20} />
                                    )}
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Organizations Card */}
                <Card className="mb-4">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Organisationer
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Box mb={3}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                            >
                                Nuvarande organisationer
                            </Typography>
                            {isLoadingData ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                    }}
                                >
                                    <Skeleton
                                        variant="rounded"
                                        width={100}
                                        height={24}
                                    />
                                    <Skeleton
                                        variant="rounded"
                                        width={120}
                                        height={24}
                                    />
                                    <Skeleton
                                        variant="rounded"
                                        width={90}
                                        height={24}
                                    />
                                </Box>
                            ) : currentUserOrganizations.length > 0 ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                    }}
                                >
                                    {currentUserOrganizations.map((org) => (
                                        <Chip
                                            key={org.id}
                                            label={org.name}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.disabled"
                                    fontStyle="italic"
                                >
                                    Användaren tillhör inga organisationer
                                </Typography>
                            )}
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel>Redigera organisationer</InputLabel>
                            <Select
                                multiple
                                value={formData.organizationIds}
                                onChange={handleOrgChange}
                                input={
                                    <OutlinedInput label="Redigera organisationer" />
                                }
                                renderValue={(selected) => (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.5,
                                        }}
                                    >
                                        {selected.map((id) => (
                                            <Chip
                                                key={id}
                                                label={getOrgNameById(id)}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                )}
                            >
                                {organizationList.map((org) => (
                                    <MenuItem key={org.id} value={org.id}>
                                        {org.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Button
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={handleSaveOrganizations}
                                disabled={
                                    !hasOrganizationChanges() || isSavingOrgs
                                }
                                startIcon={
                                    isSavingOrgs ? (
                                        <CircularProgress
                                            size={16}
                                            color="inherit"
                                        />
                                    ) : undefined
                                }
                            >
                                {isSavingOrgs
                                    ? 'Sparar...'
                                    : 'Spara organisationer'}
                            </Button>
                        </FormControl>
                    </CardContent>
                </Card>

                {/* Roles Card */}
                <Card className="mb-4">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Roller
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {isLoadingData ? (
                            <Box>
                                <Skeleton
                                    variant="text"
                                    width="30%"
                                    height={42}
                                    sx={{ mb: 1 }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="35%"
                                    height={42}
                                    sx={{ mb: 1 }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="40%"
                                    height={42}
                                    sx={{ mb: 1 }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="32%"
                                    height={42}
                                />
                            </Box>
                        ) : (
                            <FormGroup>
                                {mergedRoles.map((role) => (
                                    <FormControlLabel
                                        key={role.roleId}
                                        control={
                                            <Checkbox
                                                disabled={
                                                    role.roleName === 'Basic' ||
                                                    isSavingRoles
                                                }
                                                name={role.roleName}
                                                checked={role.enabled}
                                                onChange={handleRoleChange}
                                            />
                                        }
                                        label={t(role.roleName)}
                                    />
                                ))}
                            </FormGroup>
                        )}
                    </CardContent>
                </Card>

                {/* Status and Actions Card */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Status och åtgärder
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {isLoadingData ? (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={2}
                            >
                                <Skeleton
                                    variant="rounded"
                                    width={200}
                                    height={32}
                                />
                                <Skeleton
                                    variant="rounded"
                                    width={180}
                                    height={32}
                                />
                            </Box>
                        ) : (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={2}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Typography variant="body1">
                                        Status:
                                    </Typography>
                                    <Chip
                                        label={isActive ? 'Aktiv' : 'Inaktiv'}
                                        color={isActive ? 'success' : 'default'}
                                        size="small"
                                    />
                                    <Switch
                                        onChange={toggleUserStatus}
                                        checked={isActive}
                                        size="small"
                                        disabled={isTogglingStatus}
                                    />
                                    {isTogglingStatus && (
                                        <CircularProgress size={20} />
                                    )}
                                </Box>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleOpenConfirmDialog}
                                    size="small"
                                    disabled={isSendingEmail}
                                    startIcon={
                                        isSendingEmail ? (
                                            <CircularProgress
                                                size={16}
                                                color="inherit"
                                            />
                                        ) : undefined
                                    }
                                >
                                    {isSendingEmail
                                        ? 'Skickar...'
                                        : 'Skicka välkomstmail igen'}
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Dialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Skicka välkomstmail?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        När du skickar välkomstmailet kommer användarens
                        lösenord och PIN-kod att återställas och skickas i
                        mailet.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog} color="primary">
                        Avbryt
                    </Button>
                    <Button
                        onClick={handleResendWelcomeEmail}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        Skicka
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
export default UserView;
